import React, { useEffect, useState } from 'react';
import { CreateFormFeatureDialog } from 'components';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    consentContractConfig,
    createConsentContractDefaults,
    CreateConsentContractFormData,
    CreateConsentContractSchema,
    getCombinedOptionsData,
    JWTPropertiesErrorMessage,
} from './create-or-update-consent-contract-helper';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { getAvailableAttributeList } from 'lib';
import {
    useCreateConsentContract,
    useDictionaryDetails,
    useUserGroupList,
    useWalletList,
} from 'hooks';
import moment from 'moment';
import { useAlert, useAuth } from '../../../core';
import { CONSENT_CONTRACT_PATHS } from 'constants/consent-contracts-paths';
import { ConsentContractForm } from './ConsentContractForm';

export const CreateConsentContract = (): React.JSX.Element => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const chain = queryParams.get('chain');
    const { showAlert, hideAlert } = useAlert();
    const { getToken, getUserEmail, getUserName } = useAuth();
    const {
        handleSubmit,
        control,
        watch,
        setValue,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<CreateConsentContractFormData>({
        mode: 'all',
        resolver: yupResolver(CreateConsentContractSchema),
        defaultValues: createConsentContractDefaults,
    });
    const [attributeListAsString, setAttributeListAsString] = useState<
        string[]
    >([]);
    const [optionsData, setOptionsData] = useState<any[]>([]);
    const [jwtPropertiesValue, setJwtPropertiesValue] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { mutate: createConsentContract } = useCreateConsentContract();
    const onSuccess = (data: any): void => {
        data?.attributes.sort((a: any, b: any) => {
            return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        });
        setAttributeListAsString(getAvailableAttributeList(data?.attributes));
    };
    useDictionaryDetails({
        dictionaryId: chain,
        onLoadSuccess: onSuccess,
    });
    const { data: walletsData, isLoading: walletsLoading } = useWalletList();
    const { data: userGroupsData, isLoading: userGroupsLoading } =
        useUserGroupList();

    useEffect(() => {
        const userEmail = getUserEmail();
        const userName = getUserName();
        let combinedOptionsData: any[] = [];
        if (!walletsLoading && !userGroupsLoading) {
            combinedOptionsData = getCombinedOptionsData(
                walletsData,
                userGroupsData,
                userEmail,
                userName
            );
            setOptionsData(combinedOptionsData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [walletsLoading, userGroupsLoading]);

    useEffect(() => {
        setValue('to', []);
        setValue('jwtProperties', '');
        setJwtPropertiesValue('');
        clearErrors('to');
        clearErrors('jwtProperties');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watch('share')]);

    const onSubmit = (data: CreateConsentContractFormData): any => {
        let jwtData;
        if (watch('share') !== 'to') {
            try {
                jwtData = JSON.parse(data.jwtProperties);
                if (!Object.keys(jwtData).length) {
                    setError('jwtProperties', {
                        message: JWTPropertiesErrorMessage,
                    });
                    return;
                }
            } catch {
                setError('jwtProperties', {
                    message: JWTPropertiesErrorMessage,
                });

                return;
            }
        }
        if (data.start && data.until) {
            const startDate = moment(data.start).utc();
            const endDate = moment(data.until).utc();

            if (endDate.isBefore(startDate)) {
                showAlert({
                    severity: 'error',
                    variant: 'filled',
                    closable: true,
                    onClose: hideAlert,
                    children: 'End date occurs before start date.',
                });
                return;
            }
        }

        setIsLoading(true);
        createConsentContract(
            {
                contract: {
                    consentContract: {
                        ...(watch('share') === 'to'
                            ? {
                                  to:
                                      watch('to').length === optionsData.length
                                          ? '*'
                                          : data.to.map((item: any) => item.id),
                              }
                            : {
                                  jwtProperties: jwtData,
                              }),
                        description: data.description,
                        only: data.only,
                        start:
                            data.start === null
                                ? ''
                                : moment(data.start).utc().format(),
                        until:
                            data.until === null
                                ? ''
                                : moment(data.until).utc().format(),
                        when: data.when,
                    },
                },
                chain: chain as string,
                token: getToken(),
            },
            {
                onSuccess: () => {
                    setIsLoading(false);
                    navigate(CONSENT_CONTRACT_PATHS.LANDING_PAGE, {
                        state: chain,
                    });
                },
                onError: () => {
                    setIsLoading(false);
                },
            }
        );
    };

    const handleCloseOrBackButtonClick = (): void => {
        navigate(CONSENT_CONTRACT_PATHS.LANDING_PAGE, { state: chain });
    };

    const onJwtPropertiesValueChange = React.useCallback((value: any) => {
        setJwtPropertiesValue(value);
        setValue('jwtProperties', value);
        clearErrors('jwtProperties');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <CreateFormFeatureDialog
            buttonText='Save'
            handleSubmit={handleSubmit(onSubmit)}
            isLoading={isLoading}
            handleCloseButton={handleCloseOrBackButtonClick}
            handleBackButton={handleCloseOrBackButtonClick}
            listConfig={consentContractConfig}
        >
            <ConsentContractForm
                control={control}
                errors={errors}
                watch={watch}
                optionsData={optionsData}
                setValue={setValue}
                jwtPropertiesValue={jwtPropertiesValue}
                onJwtPropertiesValueChange={onJwtPropertiesValueChange}
                attributeListAsString={attributeListAsString}
                disablePastStartDate={true}
                disablePastEndDate={false}
            />
        </CreateFormFeatureDialog>
    );
};
